import { Pill, Tooltip } from '@/components/atoms'
import { RadioOptionPicker } from '@/components/common/Form/RadioOptionPicker/RadioOptionPicker'
import { Option } from '@/components/common/Form/types'
import { TotalEmissionsReportSettings, TotalEmissionsReportType } from '@/hooks'
import { GetSustainabilityTotalEmissionsRequestParams } from '@/hooks'
import { useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSustainabilityDisplayManager } from './useSustainabilityDisplayManager'
import useSustainabilityStore from '../../stores/useSustainabilityStore/useSustainabilityStore'
import { ALERT_DESCRIPTION_MAP } from '@/utils/constants'
import { mapSustainabilityParamsToFilters } from '../../utils/mapSustainabilityParamsToFilters'

type DisplaySettingsProps = {
    reportSettings: TotalEmissionsReportSettings
    onChange: (type: TotalEmissionsReportSettings['type']) => void
    filters: GetSustainabilityTotalEmissionsRequestParams
}

export const DisplaySettings: React.FC<DisplaySettingsProps> = ({
    reportSettings,
    onChange,
    filters,
}) => {
    const { t } = useTranslation('translations')
    const mappedFilters = mapSustainabilityParamsToFilters(filters)
    const { showAlert, commoditiesCount, emissionsCount } =
        useSustainabilityDisplayManager(reportSettings, mappedFilters)
    const pillRef = useRef<HTMLDivElement>(null)

    const setHoveredDisplaySetting = useSustainabilityStore(
        (state) => state.totalEmissions.setHoveredDisplaySetting
    )

    const onMouseEnter = useCallback(
        (display: TotalEmissionsReportType) => {
            setHoveredDisplaySetting(display)
        },
        [setHoveredDisplaySetting]
    )

    const onMouseLeave = useCallback(() => {
        setHoveredDisplaySetting(null)
    }, [setHoveredDisplaySetting])

    const options = useMemo(() => {
        return [
            {
                value: 'emissionTypes',
                label: t(
                    'sustainability.report_settings.display.emission_types'
                ),
                count: emissionsCount,
            },
            {
                value: 'commodities',
                label: t('sustainability.report_settings.display.commodities'),
                count: commoditiesCount,
            },
        ]
    }, [t, commoditiesCount, emissionsCount])

    const pillContent = (option: Option) =>
        'count' in option && typeof option.count === 'number'
            ? option.count
            : undefined

    const renderLabel = useCallback(
        (option: Option) => {
            return (
                <div
                    className="flex items-center gap-2"
                    onMouseEnter={() => {
                        onMouseEnter(option.value as TotalEmissionsReportType)
                    }}
                    onMouseLeave={onMouseLeave}
                >
                    <div className="text-label-small text-regular">
                        {option.label}
                    </div>
                    <Pill value={pillContent(option)} />
                </div>
            )
        },
        [onMouseEnter, onMouseLeave]
    )

    const renderValueLabel = useCallback(
        (option: Option) => {
            return (
                <div className="flex items-center gap-2">
                    <div className="overflow-hidden truncate text-ellipsis text-azure group-hover:text-blue-600 group-active:text-blue-700 group-disabled:text-disabled">
                        {option.label}
                    </div>
                    <div ref={pillRef}>
                        <Tooltip
                            content={t(
                                ALERT_DESCRIPTION_MAP[
                                    option.value as TotalEmissionsReportType
                                ]
                            )}
                            name="sustainability-alert-tooltip"
                            show={showAlert}
                        >
                            <Pill
                                value={pillContent(option)}
                                icon={showAlert ? 'info' : undefined}
                            />
                        </Tooltip>
                    </div>
                </div>
            )
        },
        [showAlert, t]
    )

    return (
        <RadioOptionPicker
            name="display-type"
            label={t('sustainability.report_settings.display.label')}
            value={reportSettings.type}
            options={options}
            onChange={(type: string) => {
                onChange(type as TotalEmissionsReportSettings['type'])
            }}
            renderLabel={renderLabel}
            renderValueLabel={renderValueLabel}
            className={
                reportSettings.type === 'commodities'
                    ? '!border-azure !shadow-inset-azure'
                    : undefined
            }
        />
    )
}
